import { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import {
  Button,
  Col,
  Divider,
  Form,
  Modal,
  notification,
  Row,
  Skeleton,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import DiagnosticsForm from '@/components/Movements/Diagnostics/Form';
import DiagnosticsPartList from '@/components/Movements/Diagnostics/PartsList';
import {
  useDiagnosticDetail,
  useDiagnosticParts,
  useDiagnosticRemove,
  useDiagnosticUpdate,
} from '@/core/domain/workorder/Diagnostics/Diagnostics.hooks';
import { PageProps } from '@/types/PageProps';
import {
  DiagnosticFormValues,
  DiagnosticUpdate,
} from '@/core/domain/workorder/Diagnostics/Diagnostics.types';
import isAxiosError from '@/utils/retornator/isAxiosError';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { SaveButton, TabContentWrapper } from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import handleFormChange from '@/hooks/handleFormChange';
import DiagnosticFooter from '@/components/Movements/Diagnostics/DiagnosticFooter';
import moment from 'moment';
import { DateTimeFormat } from '@/utils/moment';

const DiagnosticDetailsPage = ({ route }: PageProps) => {
  const { diagnosticId } = useRouteMatch<{ diagnosticId: string }>().params;
  const [form] = Form.useForm<DiagnosticFormValues>();
  const history = useHistory();
  const intl = useIntl();
  const [formChange, setFormChange] = useState({});
  const disabled = !Object.keys(formChange).some((key) => !!key);

  const { data: partsData } = useDiagnosticParts();
  const parts = flatInfiniteData(partsData);

  const { data, isFetching } = useDiagnosticDetail(Number(diagnosticId));
  const updateMutation = useDiagnosticUpdate();
  const diagnosticRemove = useDiagnosticRemove();

  const { setParamLabel } = useBreadcrumbs();

  useEffect(() => {
    if (data) {
      setParamLabel(':diagnosticId', data?.customer.name);

      form.setFieldsValue({
        companyId: data.company.id,
        clientObservation: data.clientObservation,
        items: parts?.map((part) => ({
          partId: part.id,
          status: data.items.find((item) => item.partId === part.id)?.status,
        })),
        vehicle: { ...data.vehicle, customerId: data.customer.id },
        entryDate: data?.entryDate ? moment(data?.entryDate) : undefined,
      });
    }
  }, [data]);

  const handleSaveDiagnostic = async () => {
    try {
      if (!data) {
        throw new Error('Não há dados de diagnostico');
      }

      const { vehicle, ...values } = await form.validateFields();

      const obj: DiagnosticUpdate = {
        ...values,
        id: data.id,
        vehicleId: vehicle.id,
        entryDate: values.entryDate?.format(DateTimeFormat),
      };

      await updateMutation.mutateAsync(obj);
    } catch (err) {
      if (!isAxiosError(err)) {
        notification.error({
          message: intl.formatMessage({
            id: 'diagnostics.update.notification.error.title',
          }),
          description: intl.formatMessage({
            id: 'diagnostics.update.notification.error.description',
          }),
        });
      }
    }
  };

  const showRemoveModal = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'diagnostics.modal.remove.title',
      }),
      content: intl.formatMessage({
        id: 'diagnostics.modal.remove.content',
      }),
      okType: 'danger',
      okText: intl.formatMessage({
        id: 'commons.list.action.button.remove',
      }),
      onOk: () =>
        diagnosticRemove
          .mutateAsync(Number(diagnosticId))
          .then(() => history.push('/app/movements/diagnostics/')),
    });
  };

  return (
    <TabContentWrapper
      title={route.name}
      extra={
        <SpaceForButton>
          <Button
            block
            danger
            onClick={() => showRemoveModal()}
            disabled={data?.generateWorkOrder}
          >
            <FormattedMessage id="commons.form.button.remove" />
          </Button>

          <SaveButton
            block
            onClick={() => handleSaveDiagnostic()}
            loading={updateMutation.isLoading}
            disabled={data?.generateWorkOrder}
          >
            <FormattedMessage id="commons.form.button.save" />
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Skeleton active loading={isFetching || updateMutation.isLoading}>
        <Form.Provider
          onFormChange={(name, info) =>
            handleFormChange(name, info, setFormChange)
          }
        >
          <Form form={form} name="diagnosticGeneralForm">
            <Row gutter={[36, 16]}>
              <Col xs={24} md={12}>
                <DiagnosticsForm form={form} />
              </Col>
              <Col xs={24} md={12}>
                <DiagnosticsPartList />
              </Col>
            </Row>
          </Form>
        </Form.Provider>

        <Divider />

        <DiagnosticFooter disabled={disabled} diagnostic={data} form={form} />
      </Skeleton>
    </TabContentWrapper>
  );
};

export default DiagnosticDetailsPage;
